import React from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageWithHeader from '../common/PageWithHeader';
import LoadingCover from '../common/LoadingCover';
import { useParams } from 'react-router-dom';
import { useRouter } from '../../hooks/useRouter';
import { useGetBSCByIdQuery } from '../../redux/balancedScorecardApi';
import { useGetUserByIdQuery } from '../../redux/vmsApi';
import { PERFORMANCE_RATINGS } from './bscTypes';

// Helper to get grade color
const getGradeColor = (grade: string): string => {
  const rating = PERFORMANCE_RATINGS.find(r => r.grade === grade);
  return rating?.colorCode || '#9e9e9e';
};

// Helper to get correct bonus entitlement from score (not stored value)
const getBonusFromScore = (score: number): number => {
  const rating = PERFORMANCE_RATINGS.find(
    r => score >= r.minPercentage && score <= r.maxPercentage
  );
  return Math.min(rating?.bonusEntitlement || 0, 100);
};

// Helper to format date
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Helper to get status color
const getStatusColor = (status: string): 'default' | 'warning' | 'info' | 'success' | 'error' => {
  switch (status) {
    case 'draft':
      return 'default';
    case 'submitted':
      return 'info';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const AdminBSC: React.FC = () => {
  const params = useParams();
  const router = useRouter();

  const { data: bscData, isLoading, error } = useGetBSCByIdQuery({ id: params?.uuid ?? '' });
  
  // Get the employeeId from bscData, with a fallback to empty string
  const employeeId = bscData?.employeeId ?? '';
  
  const { data: userData, isLoading: userIsLoading } = useGetUserByIdQuery(
    { id: employeeId },
    { skip: !employeeId }
  );

  if (isLoading || (employeeId && userIsLoading)) {
    return <LoadingCover />;
  }

  if (error || !bscData) {
    return (
      <PageWithHeader>
        <Box padding={5}>
          <Button color="primary" onClick={() => router.push('/reviews/admin')}>
            <ArrowBackIcon />
            <Typography color="primary" align="left" sx={{ ml: 1 }}>
              Admin Dashboard
            </Typography>
          </Button>
          <Paper elevation={3} sx={{ padding: 4, textAlign: 'center', mt: 2 }}>
            <Typography color="error">BSC not found</Typography>
          </Paper>
        </Box>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader>
      <Box padding={5}>
        <Button color="primary" onClick={() => router.push('/reviews/admin')}>
          <ArrowBackIcon />
          <Typography color="primary" align="left" sx={{ ml: 1 }}>
            Admin Dashboard
          </Typography>
        </Button>
        <Paper elevation={3} sx={{ padding: 4, mt: 2 }}>
          {/* Header with employee info and status */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Balanced Scorecard
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {userData?.name || bscData.employeeName || 'Employee'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                label={bscData.status || 'draft'}
                color={getStatusColor(bscData.status || 'draft')}
                size="medium"
              />
              <Chip
                label={`Grade: ${bscData.finalGrade || '-'}`}
                sx={{
                  bgcolor: getGradeColor(bscData.finalGrade || ''),
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            </Stack>
          </Stack>

          {/* Summary Cards */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={4} flexWrap="wrap">
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' } }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  Period
                </Typography>
                <Typography fontWeight="bold">{bscData.reviewPeriod || '-'}</Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' } }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  Total Score
                </Typography>
                <Typography fontWeight="bold">
                  {bscData.totalWeightedScore?.toFixed(1) ?? 0}%
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' } }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  Bonus Entitlement
                </Typography>
                <Typography fontWeight="bold">{getBonusFromScore(bscData.totalWeightedScore ?? 0)}%</Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' } }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  Job Title
                </Typography>
                <Typography fontWeight="bold">{bscData.jobDescriptionTitle || '-'}</Typography>
              </Paper>
            </Box>
          </Stack>

          {/* Submission Info */}
          {bscData.submittedAt && (
            <Paper sx={{ p: 2, mb: 4, bgcolor: 'info.50' }} variant="outlined">
              <Typography variant="body2" color="text.secondary">
                Submitted on: <strong>{formatDate(bscData.submittedAt)}</strong>
              </Typography>
              {bscData.reviewedAt && (
                <Typography variant="body2" color="text.secondary">
                  Reviewed on: <strong>{formatDate(bscData.reviewedAt)}</strong>
                </Typography>
              )}
              {bscData.managerComments && (
                <Box mt={1}>
                  <Typography variant="body2" color="text.secondary">
                    Manager Comments:
                  </Typography>
                  <Typography variant="body2">{bscData.managerComments}</Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Perspectives Breakdown */}
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Perspectives Breakdown
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>
                    <strong>Perspective</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Weight</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Score</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Weighted Score</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bscData.perspectives?.map((perspective, index) => (
                  <TableRow key={index}>
                    <TableCell>{perspective.name || '-'}</TableCell>
                    <TableCell align="right">{perspective.weight ?? 0}%</TableCell>
                    <TableCell align="right">
                      {((perspective.perspectiveTotal || 0) / (perspective.weight || 1) * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell align="right">
                      {perspective.perspectiveTotal?.toFixed(1) || 0}%
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={2}>
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell align="right" colSpan={2}>
                    <strong>{bscData.totalWeightedScore?.toFixed(1) || 0}%</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* KRA Details per Perspective */}
          {bscData.perspectives?.map((perspective, pIndex) => (
            <Box key={pIndex} mb={3}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {perspective.name || 'Perspective'} ({perspective.weight ?? 0}%)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>
                        <strong>Goal</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Measure</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Weight</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Target</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Actual</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Performance</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Score</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {perspective.kraScores?.map((kra, kIndex) => (
                      <TableRow 
                        key={kIndex}
                        sx={kra.isExtraDuty ? { 
                          bgcolor: '#fffbf0',
                          '& td': { borderBottom: '2px solid #ffc107' }
                        } : undefined}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {kra.isExtraDuty && (
                              <Chip
                                icon={<StarIcon sx={{ fontSize: 14 }} />}
                                label="EXTRA"
                                size="small"
                                sx={{ 
                                  bgcolor: '#ffc107', 
                                  color: '#5d4037',
                                  fontWeight: 'bold',
                                  fontSize: '0.65rem',
                                  height: 20,
                                  '& .MuiChip-icon': { color: '#5d4037' },
                                  '& .MuiChip-label': { px: 0.5 }
                                }}
                              />
                            )}
                            {kra.objective || '-'}
                          </Box>
                        </TableCell>
                        <TableCell>{kra.measure || '-'}</TableCell>
                        <TableCell align="right">{kra.weight ?? 0}%</TableCell>
                        <TableCell align="right">{kra.targetValue ?? '-'}</TableCell>
                        <TableCell align="right">{kra.actualValue ?? '-'}</TableCell>
                        <TableCell align="right">{kra.performance?.toFixed(1) ?? '0'}%</TableCell>
                        <TableCell align="right">{kra.weightedScore?.toFixed(2) ?? '0'}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </Paper>
      </Box>
    </PageWithHeader>
  );
};

export default AdminBSC;
